import axios from 'axios'
import { createUserWithEmailAndPassword, getIdToken } from 'firebase/auth'
import { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetCities } from '../../../hooks/api/cities/useGetCities'
import { useGetCountries } from '../../../hooks/api/countries/useGetCountries'
import { useGetProvinces } from '../../../hooks/api/provinces/useGetProvinces'
import {
  APIError,
  CityData,
  CountryData,
  PaginatedData,
  ProvinceData,
} from '../../../types/interfaces'
import { PROVINCES_CLOSED_FOR_REGISTRATION } from '../../data/provinces'
import { auth } from '../../firebase/config'
import { SignUpFormType, SnackBarAlertType } from '../../types/interface'
import { useAuthContext } from './useAuthContext'

export const useSignup = () => {
  const { t } = useTranslation()
  const { handleAppleSSO, handleGoogleSSO } = useAuthContext()
  const [selectedCountry, setSelectedCountry] = useState<number>()
  const [selectedProvince, setSelectedProvince] = useState<number>()
  const [selectedCityName, setSelectedCityName] = useState<string>('')
  const [open, setOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(true)
  const [username, setUsername] = useState('')
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })

  const initialValues: SignUpFormType = useMemo(
    () => ({
      city: '',
      country: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      province: '',
    }),
    []
  )

  const provincesFilters = useMemo(
    () => ({
      filter: {
        country_id: `eq|${selectedCountry}`,
      },
    }),
    [selectedCountry]
  )

  const citiesFilters = useMemo(
    () => ({
      filter: {
        province_id: `eq|${selectedProvince}`,
      },
    }),
    [selectedProvince]
  )

  const mapAndSortItems = (
    items: CityData[] | CountryData[] | ProvinceData[]
  ) =>
    items
      .map((item) => ({
        id:
          (item as CountryData | ProvinceData).id || (item as CityData).city_id,
        name: item.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

  const selectCountries = useCallback(
    (data: PaginatedData<CountryData[]>) => mapAndSortItems(data.items),
    []
  )

  const selectProvinces = useCallback(
    (data: PaginatedData<ProvinceData[]>) =>
      mapAndSortItems(
        data.items.filter(
          (p) => !PROVINCES_CLOSED_FOR_REGISTRATION.includes(p.id)
        )
      ),
    []
  )

  const selectCities = useCallback(
    (data: PaginatedData<CityData[]>) => mapAndSortItems(data.items),
    []
  )

  const { data: countries } = useGetCountries({
    noAuth: true,
    select: selectCountries,
  })

  const { data: provinces } = useGetProvinces({
    enabled: !!selectedCountry,
    filters: provincesFilters,
    noAuth: true,
    select: selectProvinces,
  })

  const { data: cities } = useGetCities({
    enabled: !!selectedProvince,
    filters: citiesFilters,
    noAuth: true,
    select: selectCities,
  })

  const handleTooltipClose = () => {
    setTooltipOpen(false)
  }

  const extractErrorMessage = (error: unknown): string => {
    // Axios error - APIError.message may be an array or string
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as APIError
      // APIError.message could be an array or a string
      if (Array.isArray(apiError?.message)) {
        return apiError.message[0] || 'An error occurred during registration'
      }
      return (
        apiError?.error ||
        apiError?.message ||
        error.message ||
        'An error occurred during registration'
      )
    }
    if (error instanceof Error) {
      return error.message
    }
    return 'An error occurred during registration'
  }

  const registerFirebaseUser = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const firebaseUser = userCredential.user
    const firebaseToken = await getIdToken(firebaseUser)
    return { firebaseToken, firebaseUser }
  }

  const sendVerificationEmail = async (firebaseToken: string) => {
    await axios.post(
      `${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/verification-email`,
      {},
      { headers: { Authorization: firebaseToken } }
    )
  }

  const registerDriverApiUser = async (values: SignUpFormType, firebaseToken: string) => {
    await axios.post(
      `${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/register`,
      {
        city: values.city,
        country: values.country,
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        province: values.province,
      },
      {
        headers: {
          Authorization: firebaseToken,
        },
      }
    )
  }

  const handleSignUp = async (values: SignUpFormType) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_DRIVER_API_BASE_URL}/v2/auth/validate-pre-signup`,
        {
          params: {
            email: values.email,
          },
        }
      )

      if (!response.data?.is_valid) {
        setAlertEvent({
          message: t('auth.register.email_already_exists'),
          open: true,
          type: 'error',
        })
        return
      }

      // Register user in Firebase
      const { firebaseToken } = await registerFirebaseUser(values.email, values.password)

      // Register user in driver API
      await registerDriverApiUser(values, firebaseToken)

      // Send branded verification email via backend
      await sendVerificationEmail(firebaseToken)

      setUsername(values.email)
      setOpen(true)
    } catch (error: unknown) {
      setAlertEvent({
        message: extractErrorMessage(error),
        open: true,
        type: 'error',
      })
    }
  }

  const handleSignupWithGoogle = async () => {
    try {
      await handleGoogleSSO()
    } catch (error) {
      let errorMessage = extractErrorMessage(error);

      // override error message for last name in google account
      if (errorMessage === "last_name should not be empty") {
        errorMessage = "Please update the last name in your Google account and try registering again";
      }
      setAlertEvent({
        message: errorMessage,
        open: true,
        type: 'error',
      })
    }
  }

  const handleSignupWithApple = async () => {
    try {
      await handleAppleSSO()
    } catch (error) {
      setAlertEvent({
        message: extractErrorMessage(error),
        open: true,
        type: 'error',
      })
    }
  }

  const handleClose = (_event: Event | SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  const handleChangeCountry = (value: number) => {
    setSelectedCountry(value)
  }

  const handleChangeProvince = (value: number) => {
    setSelectedProvince(value)
  }

  const handleChangeCity = (cityName: string) => {
    setSelectedCityName(cityName)
  }

  return {
    alertEvent,
    cities,
    countries,
    handleChangeCity,
    handleChangeCountry,
    handleChangeProvince,
    handleClose,
    handleSignUp,
    handleSignupWithApple,
    handleSignupWithGoogle,
    handleTooltipClose,
    initialValues,
    open,
    provinces,
    selectedCityName,
    selectedCountry,
    selectedProvince,
    setOpen,
    tooltipOpen,
    username,
  }
}
